import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';

export interface UsePricingFilterPanelResult {
  close: () => void;
  isOpen: boolean;
  open: () => void;
}

export const usePricingFilterPanel = (): UsePricingFilterPanelResult => {
  const [isOpen, setIsOpen] = useState(false);
  const { layoutSize } = useLayoutContext();
  const isDesktopLayout =
    layoutSize !== LayoutSize.SMALL && layoutSize !== LayoutSize.SMALL_TABLET;
  const priorSidebarCollapsedRef = useRef<boolean | null>(null);

  /** Deliberately not gated on the layout: the ref is only ever written on
   *  desktop, so a resize while open can't strand the nav collapsed. */
  const restoreSidebar = useCallback(() => {
    if (priorSidebarCollapsedRef.current === null) {
      return;
    }
    useExperienceUIStore
      .getState()
      .setSidebarCollapsed(priorSidebarCollapsedRef.current);
    priorSidebarCollapsedRef.current = null;
  }, []);

  const open = useCallback(() => {
    if (isDesktopLayout) {
      const { isSidebarCollapsed, setSidebarCollapsed } =
        useExperienceUIStore.getState();
      // A repeat open must not capture the collapse we ourselves applied.
      if (priorSidebarCollapsedRef.current === null) {
        priorSidebarCollapsedRef.current = isSidebarCollapsed;
      }
      setSidebarCollapsed(true);
    }
    setIsOpen(true);
  }, [isDesktopLayout]);

  const close = useCallback(() => {
    restoreSidebar();
    setIsOpen(false);
  }, [restoreSidebar]);

  // The collapse is persisted, so leaving the screen with the panel open
  // would otherwise strip the user's expanded nav in every later session.
  useEffect(() => {
    return () => {
      restoreSidebar();
    };
  }, [restoreSidebar]);

  return { close, isOpen, open };
};
